import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Loader2, Shield, ChevronRight, MapPin, Eraser, FileSignature } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BlogLeadForm from "@/components/BlogLeadForm";
import FirmInboundCTA from "@/components/FirmInboundCTA";
import SpotlightBanner from "@/components/SpotlightBanner";
import { getSeoLanding } from "@/lib/api";

export default function SeoLanding() {
    const { state, practice } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        getSeoLanding(state, practice)
            .then(setData)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [state, practice]);

    // SEO meta tags (also helps WhatsApp / iMessage / Slack previews)
    useEffect(() => {
        if (!data) return;
        const prevTitle = document.title;
        document.title = data.title;
        const upsert = (sel, attrs) => {
            let el = document.head.querySelector(sel);
            if (!el) {
                el = document.createElement("meta");
                Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            } else {
                Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            }
        };
        upsert('meta[name="description"]', { name: "description", content: data.meta_description });
        upsert('meta[property="og:title"]', { property: "og:title", content: data.title });
        upsert('meta[property="og:description"]', { property: "og:description", content: data.meta_description });
        upsert('meta[property="og:url"]', { property: "og:url", content: data.canonical_url });
        upsert('meta[property="og:type"]', { property: "og:type", content: "website" });
        upsert('link[rel="canonical"]', { rel: "canonical", href: data.canonical_url });
        return () => {
            document.title = prevTitle;
        };
    }, [data]);

    if (loading) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="text-center py-32">
                    <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30]" />
                </div>
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="text-center py-32">
                    <div className="font-anton text-3xl uppercase">Page not found.</div>
                    <Link to="/hpm" className="btn-brutal mt-6 inline-flex">
                        BACK HOME
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            {/* HERO */}
            <section className="border-b-2 border-white/20" data-testid="seo-hero">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 sm:py-24">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4 flex items-center gap-2">
                        <MapPin size={11} /> {data.state_name.toUpperCase()} · {data.practice_label.toUpperCase()}
                    </div>
                    <h1
                        className="font-anton uppercase leading-[0.9] text-5xl sm:text-7xl md:text-8xl"
                        data-testid="seo-headline"
                    >
                        {data.headline.split(" in ")[0]}
                        <br />
                        in <span className="text-[#FF3B30]">{data.state_name}</span>.
                    </h1>
                    <p className="mt-6 max-w-2xl font-sans text-white/75 text-base sm:text-lg leading-relaxed">
                        {data.subheadline}
                    </p>

                    {/* Two-column hero: copy on left, lead form on right */}
                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        <div>
                            {/* State spotlight slot — runs above intro copy */}
                            <div className="mb-6">
                                <SpotlightBanner
                                    slot={`state:${data.state_slug}`}
                                    label={`${data.state_name.toUpperCase()} FIRM OF THE MONTH`}
                                />
                            </div>
                            <p className="font-sans text-white/85 text-base leading-relaxed">
                                {data.intro}
                            </p>

                            <div className="mt-8" data-testid="seo-firm-trial">
                                <FirmInboundCTA
                                    state={data.state_name}
                                    practice={data.practice_label}
                                    sourceSlug={`seo-${data.state_slug}-${data.practice_slug}`}
                                />
                            </div>
                        </div>

                        <BlogLeadForm
                            slug={`seo-${data.state_slug}-${data.practice_slug}`}
                            topicKey=""
                            category={data.practice_label}
                            variant="inline"
                            slot="seo"
                            accent="#FF3B30"
                        />
                    </div>
                </div>
            </section>

            {/* RELATED ARTICLES */}
            {data.related_posts?.length > 0 && (
                <section className="border-b-2 border-white/20" data-testid="seo-related-posts">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                            DEEP DIVES
                        </div>
                        <h2 className="font-anton text-4xl sm:text-5xl uppercase mb-8">
                            Plain-English answers for {data.state_name}.
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-white/20">
                            {data.related_posts.map((p, i) => (
                                <Link
                                    key={p.slug}
                                    to={`/hpm-blog/${p.slug}`}
                                    className="block p-6 border-white/20 md:[&:not(:nth-child(3n))]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:nth-child(-n+3)]:border-b-0 hover:bg-white/5 transition-colors"
                                    data-testid={`seo-related-post-${i}`}
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                                        {p.category_label || "LEGAL EDUCATION"}
                                    </div>
                                    <h3 className="font-anton text-2xl uppercase leading-tight">
                                        {p.title}
                                    </h3>
                                    {p.subtitle && (
                                        <p className="font-sans text-sm text-white/70 mt-3">{p.subtitle}</p>
                                    )}
                                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] flex items-center gap-1">
                                        READ <ChevronRight size={12} />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FREE RESOURCES */}
            {data.free_resources?.length > 0 && (
                <section className="border-b-2 border-white/20">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 flex items-center gap-2">
                            <Shield size={11} /> FREE RESOURCES IN {data.state_name.toUpperCase()}
                        </div>
                        <h2 className="font-anton text-4xl uppercase mb-6">
                            Tools you can use today.
                        </h2>
                        <ul className="space-y-3 max-w-3xl">
                            {data.free_resources.map((r, i) => (
                                <li
                                    key={i}
                                    className="font-sans text-base text-white/85 flex gap-3"
                                    data-testid={`seo-resource-${i}`}
                                >
                                    <span className="font-anton text-2xl text-[#FFCC00] leading-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* FREE AI TOOLS — 100% free, no card */}
            <section className="border-b-2 border-white/20 bg-white/[0.03]" data-testid="seo-free-tools">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-14">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3 flex items-center gap-2">
                        <Shield size={11} /> FREE AI TOOLS · NO ACCOUNT
                    </div>
                    <h2 className="font-anton text-4xl sm:text-5xl uppercase leading-[0.95] mb-3">
                        Don&apos;t want to wait?<br />Use these two right now.
                    </h2>
                    <p className="font-sans text-white/70 text-base max-w-2xl mb-8">
                        Two Claude-powered tools for {data.state_name} residents. Get a plain-English
                        read in 30 seconds. No card. No login.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-white/20">
                        <Link
                            to="/hpm-expungement"
                            data-testid="seo-tool-expungement"
                            className="block p-6 md:p-8 border-white/20 md:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:not(:last-child)]:border-b-0 hover:bg-white/5 transition-colors group"
                        >
                            <div className="flex items-start gap-4">
                                <div className="border-2 border-[#FFCC00] p-3 group-hover:bg-[#FFCC00] group-hover:text-black transition-colors">
                                    <Eraser size={22} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] mb-2">
                                        EXPUNGEMENT CHECKER
                                    </div>
                                    <h3 className="font-anton text-2xl uppercase leading-tight mb-2">
                                        Can your record be erased in {data.state_name}?
                                    </h3>
                                    <p className="font-sans text-sm text-white/70">
                                        Answer 7 questions. Get statute citations, next steps, forms,
                                        and an estimated timeline.
                                    </p>
                                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] flex items-center gap-1">
                                        RUN CHECK <ChevronRight size={12} />
                                    </div>
                                </div>
                            </div>
                        </Link>
                        <Link
                            to="/demand-letter"
                            data-testid="seo-tool-demand-letter"
                            className="block p-6 md:p-8 border-white/20 hover:bg-white/5 transition-colors group"
                        >
                            <div className="flex items-start gap-4">
                                <div className="border-2 border-[#FF3B30] p-3 group-hover:bg-[#FF3B30] group-hover:text-white transition-colors">
                                    <FileSignature size={22} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] mb-2">
                                        DEMAND LETTER GENERATOR
                                    </div>
                                    <h3 className="font-anton text-2xl uppercase leading-tight mb-2">
                                        Send a formal demand in 30 seconds.
                                    </h3>
                                    <p className="font-sans text-sm text-white/70">
                                        Fill the facts, set the amount, pick a tone. Get a print-ready
                                        pre-litigation letter you can copy or download.
                                    </p>
                                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] flex items-center gap-1">
                                        DRAFT NOW <ChevronRight size={12} />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* 10-BOT CTA */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-3">
                        OR — RUN YOUR SITUATION THROUGH THE 10 BOTS
                    </div>
                    <h2 className="font-anton text-4xl sm:text-5xl uppercase">
                        Free analysis. Real precedents.<br />
                        For <span className="text-[#FF3B30]">{data.state_name}</span> law.
                    </h2>
                    <Link to="/hpm-submit" className="btn-brutal btn-brutal-red mt-6 inline-flex">
                        START FREE 10-BOT ANALYSIS <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            <Footer disclaimer="" />
        </div>
    );
}
